import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

import { apiClient } from '../axios.js';
import { COUNTDOWN_TICK_MS } from '../constants/jumper-sessions.js';
import { resolveMutationErrorMessage } from '../lib/error.js';
import { type JumperSession, jumperSessionSchema } from '../types/jumper-session.js';
import { useIntl } from './useIntl.js';

export type CreateJumperSessionRequest = {
  detectionGroupId?: string;
  startFrequencyHz: number;
  endFrequencyHz: number;
  durationSeconds: number;
  suppressionTime: number;
  suppressionMaxCount: number;
  bandwidthHz: number;
};

export type CreateJumperSessionPayload = {
  deviceId: string;
  payload: CreateJumperSessionRequest;
};

export function useCreateJumperSession() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation({
    mutationFn: async ({
      deviceId,
      payload,
    }: CreateJumperSessionPayload): Promise<JumperSession> => {
      try {
        const { data } = await apiClient.post(`/devices/${deviceId}/jumper-sessions`, payload);
        return jumperSessionSchema.parse(data);
      } catch (error) {
        Sentry.captureException(error);
        throw new Error(
          resolveMutationErrorMessage(error, {
            capabilityMessage: intl.formatMessage({ id: 'jumperSession.dialog.capabilityMissing' }),
            fallback: intl.formatMessage({ id: 'jumperSession.dialog.genericSubmitError' }),
          }),
        );
      }
    },
    // Returned, so `mutateAsync` stays pending until the active-session query has actually
    // refetched. Unreturned, it resolved while `useActiveJumperSession` still reported `null`:
    // the inline Jumper tab would reset and re-enable itself in that window and the operator
    // could fire a duplicate start. The dialog this replaced could not show the bug — it closed
    // on success.
    //
    // One key, not two: `['jumper-sessions']` already matches every jumper query by prefix. The
    // second call this used to make, `['jumper-sessions', { deviceId }]`, matched nothing at all —
    // the active query's key is `['jumper-sessions', 'active', { deviceId }]`, whose element 1 is
    // the string `'active'`, not the params object.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jumper-sessions'] }),
  });
}

export type ActiveJumperSessionResult = {
  session: JumperSession | null;
  secondsRemaining: number;
  isFetched: boolean;
  /**
   * The last read of the active session failed. Callers that decide whether a NEW session may be
   * started must consult it: `session` falls back to the query's last successful value, which is
   * `null` for a device that has never had one, and TanStack wraps every refetch it triggers from
   * an invalidation in `catch(noop)` — so an errored refetch is otherwise indistinguishable from
   * a confirmed "no session running".
   */
  isError: boolean;
};

export function useActiveJumperSession(deviceId: string): ActiveJumperSessionResult {
  const {
    data: session,
    isFetched,
    isError,
  } = useQuery({
    queryKey: ['jumper-sessions', 'active', { deviceId }],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get(`/devices/${deviceId}/jumper-sessions/active`);
        if (data === '' || data == null) return null;
        return jumperSessionSchema.nullable().parse(data);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        Sentry.captureException(error);
        throw new Error('Failed to fetch active jumper session.');
      }
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const queryClient = useQueryClient();
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const invalidatedSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!session?.expiresAt) {
      setSecondsRemaining(0);
      return;
    }

    const sessionId = session.id;
    const expiresAt = session.expiresAt;

    const computeRemaining = () => {
      const expiry = new Date(expiresAt as string).getTime();
      const now = Date.now();
      return Math.max(0, Math.floor((expiry - now) / 1000));
    };

    const handleExpiry = () => {
      if (invalidatedSessionIdRef.current === sessionId) return;
      invalidatedSessionIdRef.current = sessionId;
      queryClient.invalidateQueries({
        queryKey: ['jumper-sessions', 'active', { deviceId }],
      });
    };

    const initial = computeRemaining();
    setSecondsRemaining(initial);
    if (initial <= 0) {
      handleExpiry();
      return;
    }

    const interval = setInterval(() => {
      const remaining = computeRemaining();
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        handleExpiry();
      }
    }, COUNTDOWN_TICK_MS);

    return () => clearInterval(interval);
  }, [session?.id, session?.expiresAt, deviceId, queryClient]);

  return { session: session ?? null, secondsRemaining, isFetched, isError };
}

export function useStopJumperSession(deviceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string): Promise<void> => {
      try {
        await apiClient.delete(`/devices/${deviceId}/jumper-sessions/${sessionId}`);
      } catch (error) {
        Sentry.captureException(error);
        const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
        throw new Error(typeof message === 'string' ? message : 'Failed to stop jumper session.');
      }
    },
    // Same contract as the start mutation above: returned so `mutateAsync` stays pending until
    // the active-session query has refetched. `JumperSessionAlert` owns the Stop button and stays
    // mounted through its own mutation — it disappears only once that query says the session is
    // gone — so an unreturned invalidation drops `isPending` while the alert still renders the
    // finished session, and a second click deletes it again.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jumper-sessions'] }),
  });
}
